import React from 'react';
import { FontIcon, DefaultButton, PrimaryButton } from "@fluentui/react";
import { getPrimaryBlueTheme, getPrimaryGreenTheme, getPrimaryRedTheme } from '../themes';
import {
    FieldFormat,
    FieldType,
    IRegion,
    ITableKeyField,
    ITableRegion,
    ITableTag,
    ITableTagField,
    TableElements,
    TableVisualizationHint,
    TagInputMode
} from '../../model/applicationState';
import TextareaAutosize from 'react-textarea-autosize';

import { strings } from "../strings";
import "./tableTagLabeling.scss";
import "../../App.scss"
import Confirm from "../../common/confirm/confirm";
import { getParameterCaseInsensitive } from "../../Utils/utils";

interface ITableTagLabelingProps {
    setTagInputMode: (addTableMode: TagInputMode, selectedTableTagToLabel?: ITableTag, selectedTableTagBody?: ITableRegion[][][]) => void;
    selectedTag: ITableTag,
    selectedRegions?: IRegion[];
    onTagClick?: (tag: ITableTag) => void;
    selectedTableTagBody: ITableRegion[][][];
    handleTableCellClick: (iTableCellIndex: number, jTableCellIndex: number) => void;
    handleTableCellMouseEnter: (regions: IRegion[]) => void
    handleTableCellMouseLeave: () => void
    addRowToDynamicTable: () => void;
    deleteRowFromDynamicTable: (rowIndex: number, tag: ITableTag) => void;
    onTableTagLabelEdit: (tagName: string, metaData: any) => void;
    updatePredictionResult: (data: any, field: any) => void;
    splitPaneWidth?: number;
    predictionResult: any;
    changePaneOrientation: () => void
}


interface ITableTagLabelingState {
    selectedRowIndex: number;
    selectedColumnIndex: number;
    rows: ITableKeyField[],
    columns: ITableKeyField[],
    selectedTableTagBody: any,
    tableTagField: ITableTagField,
}

// @connect(mapStateToProps)
export default class TableTagLabeling extends React.Component<ITableTagLabelingProps> {
    public state: ITableTagLabelingState = {
        selectedRowIndex: null,
        selectedColumnIndex: null,
        rows: this.props.selectedTag.type === FieldType.Array || this.props.selectedTag?.visualizationHint === TableVisualizationHint.Vertical ? this.props.selectedTag.fields : this.props.selectedTag.definition.fields,
        columns: this.props.selectedTag.type === FieldType.Array || this.props.selectedTag.visualizationHint === TableVisualizationHint.Vertical ? this.props.selectedTag.definition.fields : this.props.selectedTag.fields,
        selectedTableTagBody: this.props.selectedTableTagBody,
        tableTagField: this.props.predictionResult.analyzeResult.documentResults[0].fields[this.props.selectedTag.name]
    };

    private deleteRowConfirmRef = React.createRef<Confirm>();

    public componentDidMount = async () => {
        if (this.props.selectedTag.type === FieldType.Array) {
            let rows = [];
            for (let i = 0; i < this.props.selectedTableTagBody.length; i++) {
                rows.push({ fieldKey: "#" + i, fieldType: FieldType.String, fieldFormat: FieldFormat.NotSpecified });
            }
            this.setState({ rows });
        }
    }

    public static getDerivedStateFromProps = (nextProps: Readonly<ITableTagLabelingProps>, prevState: Readonly<ITableTagLabelingState>) => {
        if (nextProps.selectedTableTagBody.length !== prevState.selectedTableTagBody.length) {
            const tableTagField: ITableTagField = nextProps.predictionResult.analyzeResult.documentResults[0].fields[nextProps.selectedTag.name];
            let rows = []
            for (let i = 0; i < tableTagField.valueArray.length; i++) {
                rows.push({ fieldKey: "#" + i, fieldType: FieldType.String, fieldFormat: FieldFormat.NotSpecified });
            }
            return {
                rows,
                selectedTableTagBody: nextProps.selectedTableTagBody,
                tableTagField
            }
        }
        return null
    }

    private togglePaneOrientation = () => {
        const paneOrientation = localStorage.getItem('paneOrientation');
        if (paneOrientation) {
            if (paneOrientation == 'horizontal') {
                localStorage.setItem('paneOrientation', 'vertical');
            } else {
                localStorage.setItem('paneOrientation', 'horizontal');
            }
        } else {
            localStorage.setItem('paneOrientation', 'vertical');
        }
        this.props.changePaneOrientation();
    }

    public render() {
        const paneOrientation = localStorage.getItem('paneOrientation');
        const dockButtonText = paneOrientation == "horizontal" ? "Dock Left" : "Dock Top";
        return (
            <div className="table-labeling_container">
                <div className='d-flex'>
                    <h5 className="mb-4 table-name">
                        <span style={{ borderBottom: `4px solid ${this.props.selectedTag.color}` }}>{`${strings.tags.regionTableTags.tableLabeling.tableName}: ${this.props.selectedTag.name}`}</span>
                    </h5>
                    <DefaultButton onClick={this.togglePaneOrientation}>{dockButtonText}</DefaultButton>
                </div>

                <div className="buttons-container">
                    {this.props.selectedTag.type === FieldType.Array && <div className="add-row-button_container">
                        <PrimaryButton
                            theme={getPrimaryBlueTheme()}
                            className="add_button ml-6"
                            autoFocus={true}
                            onClick={this.addRow}
                        >
                            <FontIcon iconName="Add" className="mr-2" />
                            {strings.tags.regionTableTags.tableLabeling.buttons.addRow}
                        </PrimaryButton>
                    </div>}
                    <PrimaryButton
                        className="button-done"
                        theme={getPrimaryGreenTheme()}
                        onClick={() => {
                            this.props.setTagInputMode(TagInputMode.Basic, null, null)
                            this.props.onTableTagLabelEdit(this.props.selectedTag.name, this.state.tableTagField)
                        }}
                    >{strings.tags.regionTableTags.tableLabeling.buttons.done}
                    </PrimaryButton>
                </div>
                {(this.props.selectedTag.type === FieldType.Object && this.props.selectedTag.fields && this.props.selectedTag.definition.fields) || this.props.selectedTag.definition.fields ?
                    <div className="table-view-container">
                        <table className="viewed-table">
                            <tbody>
                                {this.getTableBody()}
                            </tbody>
                        </table>
                    </div>
                    :
                    <div>Missing fields. Please Reconfigure table.</div>
                }
                <Confirm
                    title={"Delete Row"}
                    ref={this.deleteRowConfirmRef}
                    message={"Are you sure you want to delete this row?"}
                    confirmButtonTheme={getPrimaryRedTheme()}
                    onConfirm={this.deleteRow}
                />
            </div>
        )
    }

    public getTableBody = () => {
        const table = { rows: this.state.rows, columns: this.state.columns };
        const {
            selectedTableTagBody,
            selectedRegions
        } = this.props

        const isRowDynamic = this.props.selectedTag.type === FieldType.Array;
        const hasSelectedRegions = selectedRegions ? selectedRegions.length > 0 : false

        let tableBody = null;
        if (table.rows && table.rows?.length !== 0 && table.columns.length !== 0) {
            tableBody = [];
            const rows = table[TableElements.rows];
            const columns = table[TableElements.columns];
            for (let i = 0; i < rows.length + 1; i++) {
                const tableRow = [];
                for (let j = 0; j < columns.length + 1; j++) {
                    if (isRowDynamic && rows.length > 1 && j === 0) {
                        if (i !== 0) {
                            tableRow.push(<td width={20} key={`del-${j}`} className={"row_header hidden"}>
                                <FontIcon onClick={() => this.deleteRowConfirm(i - 1)} iconName="Delete" className="delete-row-icon" />
                            </td>);
                        } else {
                            tableRow.push(<th key={`del-header-${j}`} className={`empty_header  ${isRowDynamic ? "hidden" : ""}`} />);
                        }
                    }
                    if (i === 0 && j !== 0) {
                        tableRow.push(<th key={j} className={"column_header"}>{columns[j - 1].fieldKey}</th>);
                    } else if (j === 0 && i !== 0) {
                        tableRow.push(<td width={20} key={j} className={`row_header ${isRowDynamic ? "hidden" : ""}`}>{rows[i - 1].fieldKey}</td>);
                    } else if (j === 0 && i === 0) {
                        tableRow.push(<th key={j} className={`empty_header  ${isRowDynamic ? "hidden" : ""}`} />);
                    } else {
                        tableRow.push(
                            <td
                                className={"table-cell"}
                                onClick={() => this.handleCellClick(i - 1, j - 1)} key={j}
                                onMouseEnter={() => this.handleTableCellMouseEnter(selectedTableTagBody[i - 1][j - 1])}
                                onMouseLeave={() => this.handleTableCellMouseLeave()}
                            >
                                <div className='d-flex align-items-center'>
                                    {selectedTableTagBody[i - 1][j - 1]?.find((tableRegion) => tableRegion.value === "") && <FontIcon className="draw-region-icon" iconName="FieldNotChanged" />}
                                    <TextareaAutosize
                                        maxRows={4}
                                        minRows={2}
                                        value={this.getTextValue(i - 1, columns[j - 1].fieldKey, rows[i - 1].fieldKey)}
                                        className={`tag-item-label-editable ${hasSelectedRegions ? "cursor-pointer" : "cursor-text"}`}
                                        disabled={hasSelectedRegions}
                                        onChange={ev => this.setValue(ev.target.value, i - 1, columns[j - 1].fieldKey, rows[i - 1].fieldKey)}
                                    />

                                </div>
                            </td>);
                    }
                }
                tableBody.push(<tr key={i}>{tableRow}</tr>);
            }
        }

        return tableBody
    }

    private getTextValue = (rowIndex, columnName, rowName?) => {
        const { tableTagField } = this.state;
        if (tableTagField.type === FieldType.Array) {
            if (tableTagField.valueArray[rowIndex]) {
                const value = tableTagField.valueArray[rowIndex].valueObject[columnName] ||
                    getParameterCaseInsensitive(tableTagField.valueArray[rowIndex].valueObject, columnName);
                return value && value.text;
            }
        } else if (tableTagField.type === FieldType.Object) {
            if (this.props.selectedTag.visualizationHint === TableVisualizationHint.Horizontal) {
                const value = tableTagField.valueObject[columnName].valueObject[rowName] ||
                    getParameterCaseInsensitive(tableTagField.valueObject[columnName].valueObject, rowName);
                return value && value.text;
            } else {
                const value = tableTagField.valueObject[rowName].valueObject[columnName]
                    || getParameterCaseInsensitive(tableTagField.valueObject[rowName].valueObject, columnName);
                return value && value.text;
            }
        }
    }

    private setValue = (value, rowIndex, columnName, rowName?) => {
        let { tableTagField } = this.state;
        if (tableTagField.type === FieldType.Array) {
            tableTagField.valueArray[rowIndex].valueObject[columnName].text = value
        } else if (tableTagField.type === FieldType.Object) {
            if (this.props.selectedTag.visualizationHint === TableVisualizationHint.Horizontal) {
                tableTagField.valueObject[columnName].valueObject[rowName].text = value;
            } else {
                tableTagField.valueObject[rowName].valueObject[columnName].text = value;
            }
        }
        this.props.updatePredictionResult(this.props.predictionResult, tableTagField);
        this.setState({ tableTagField })
    }

    private addRow = () => {
        this.props.addRowToDynamicTable()
    };

    private deleteRowConfirm = (rowIndex: number) => {
        this.deleteRowConfirmRef.current.open(rowIndex);
    }

    private deleteRow = (rowIndex) => {
        this.props.deleteRowFromDynamicTable(rowIndex, this.props.selectedTag)
    }

    private handleCellClick = (iToChange: number, jToChange: number) => {
        this.props.handleTableCellClick(iToChange, jToChange)
    }
    private handleTableCellMouseEnter = (regions: IRegion[]) => {
        this.props.handleTableCellMouseEnter(regions)
    }
    private handleTableCellMouseLeave = () => {
        this.props.handleTableCellMouseLeave();
    }
}
